import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Queue, EnvelopeSimple, Prohibit, CheckCircle, Clock,
  ArrowClockwise, UserPlus, CaretUp, CaretDown,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

function fmt(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return '—'; }
}

const STATUS_CONFIG = {
  waiting:   { label: 'Waiting',   bg: 'bg-blue-500/10',    text: 'text-blue-400',    border: 'border-blue-500/20',    icon: Clock },
  invited:   { label: 'Invited',   bg: 'bg-amber-500/10',   text: 'text-amber-400',   border: 'border-amber-500/20',   icon: EnvelopeSimple },
  converted: { label: 'Converted', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', icon: CheckCircle },
  rejected:  { label: 'Rejected',  bg: 'bg-red-500/10',     text: 'text-red-400',     border: 'border-red-500/20',     icon: Prohibit },
};

function WaitlistBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.waiting;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
      <Icon size={10} weight="fill" />{cfg.label}
    </span>
  );
}

export default function AdminWaitlistTab() {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortBy, setSortBy] = useState('position');
  const [sortDir, setSortDir] = useState('asc');
  const [busy, setBusy] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort_by: sortBy, sort_dir: sortDir, limit: '200' });
      if (statusFilter) params.set('status', statusFilter);
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/waitlist?${params}`),
        axios.get(`${API}/admin/waitlist/stats`),
      ]);
      setEntries(listRes.data.entries || []);
      setStats(statsRes.data || {});
      setSelectedIds(new Set());
    } catch (err) {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sortBy, sortDir]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleSort = (col) => {
    if (sortBy === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return null;
    return sortDir === 'asc' ? <CaretUp size={10} /> : <CaretDown size={10} />;
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const waitingIds = entries.filter(e => e.status === 'waiting').map(e => e.id);
    if (waitingIds.every(id => selectedIds.has(id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(waitingIds));
    }
  };

  const handleInvite = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/waitlist/${id}/invite`);
      toast.success('Invite sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    } finally {
      setBusy(false);
    }
  };

  const handleBatchInvite = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/waitlist/batch-invite`, { ids });
      toast.success(`Invited ${res.data.invited}, failed ${res.data.failed}`);
      fetchData();
    } catch (err) {
      toast.error('Batch invite failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/waitlist/${id}/reject`);
      toast.success('Entry rejected');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setBusy(false);
    }
  };

  const waitingEntries = entries.filter(e => e.status === 'waiting');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Queue size={20} className="text-amber-500" weight="fill" />
            Waitlist
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">Manage public signup queue</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-zinc-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/5 transition-all"
        >
          <ArrowClockwise size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats badges */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: '',          label: 'All',       count: stats.total || 0 },
          { key: 'waiting',   label: 'Waiting',   count: stats.waiting || 0 },
          { key: 'invited',   label: 'Invited',   count: stats.invited || 0 },
          { key: 'converted', label: 'Converted', count: stats.converted || 0 },
          { key: 'rejected',  label: 'Rejected',  count: stats.rejected || 0 },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              statusFilter === f.key
                ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                : 'bg-white/5 text-zinc-400 border-white/5 hover:bg-white/10'
            }`}
          >
            {f.label} <span className="ml-1 font-mono">{f.count}</span>
          </button>
        ))}
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <span className="text-xs text-amber-400 font-medium">{selectedIds.size} selected</span>
          <button
            onClick={handleBatchInvite}
            disabled={busy}
            className="flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium bg-amber-500 text-black hover:bg-amber-600 disabled:opacity-50 transition-colors"
          >
            <UserPlus size={12} />Invite Selected
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-zinc-500 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-[#111111] border border-white/5 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="py-3 px-4 text-left w-10">
                  <input
                    type="checkbox"
                    checked={waitingEntries.length > 0 && waitingEntries.every(e => selectedIds.has(e.id))}
                    onChange={toggleAll}
                    className="rounded border-white/20 bg-transparent"
                  />
                </th>
                <th className="py-3 px-4 text-left cursor-pointer select-none" onClick={() => toggleSort('position')}>
                  # <SortIcon col="position" />
                </th>
                <th className="py-3 px-4 text-left cursor-pointer select-none" onClick={() => toggleSort('full_name')}>
                  Name <SortIcon col="full_name" />
                </th>
                <th className="py-3 px-4 text-left cursor-pointer select-none" onClick={() => toggleSort('email')}>
                  Email <SortIcon col="email" />
                </th>
                <th className="py-3 px-4 text-left cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  Status <SortIcon col="status" />
                </th>
                <th className="py-3 px-4 text-left cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                  Joined <SortIcon col="created_at" />
                </th>
                <th className="py-3 px-4 text-left">Invited At</th>
                <th className="py-3 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="py-12 text-center text-zinc-500 text-xs">Loading…</td></tr>
              ) : entries.length === 0 ? (
                <tr><td colSpan={8} className="py-12 text-center text-zinc-500 text-xs">No waitlist entries</td></tr>
              ) : entries.map(e => (
                <tr key={e.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="py-3 px-4">
                    {e.status === 'waiting' && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(e.id)}
                        onChange={() => toggleSelect(e.id)}
                        className="rounded border-white/20 bg-transparent"
                      />
                    )}
                  </td>
                  <td className="py-3 px-4 font-mono text-zinc-400">{e.position}</td>
                  <td className="py-3 px-4 text-white font-medium">{e.full_name}</td>
                  <td className="py-3 px-4 text-zinc-400">{e.email}</td>
                  <td className="py-3 px-4"><WaitlistBadge status={e.status} /></td>
                  <td className="py-3 px-4 text-zinc-500 text-xs">{fmt(e.created_at)}</td>
                  <td className="py-3 px-4 text-zinc-500 text-xs">{fmt(e.invited_at)}</td>
                  <td className="py-3 px-4 text-right">
                    {e.status === 'waiting' && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleInvite(e.id)}
                          disabled={busy}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 disabled:opacity-50 transition-all"
                        >
                          <EnvelopeSimple size={10} />Invite
                        </button>
                        <button
                          onClick={() => handleReject(e.id)}
                          disabled={busy}
                          className="flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 disabled:opacity-50 transition-all"
                        >
                          <Prohibit size={10} />Reject
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
